import { useCallback, useRef } from 'preact/hooks';
import { resolveWidgetForProfile } from '../adapters/responsiveAdapter.js';

function token(name, fallback = 'transparent') {
  return name ? `var(--color-${name})` : fallback;
}

export function WidgetRenderer({
  widget,
  root = false,
  editMode = false,
  selectedId,
  canvasScale = 1,
  rootWidth,
  rootHeight,
  parentWidth,
  parentHeight,
  activeProfile = 'desktop',
  onSelect,
  onPreview,
  onCommit,
}) {
  const dragRef = useRef(null);
  const resolved = resolveWidgetForProfile(widget, activeProfile);
  const props = resolved.props;
  const ownWidth = root ? rootWidth : props.width * parentWidth;
  const ownHeight = root ? rootHeight : props.height * parentHeight;
  const frameStyle = {
    position: root ? 'relative' : 'absolute',
    left: root ? 0 : `${(props.x || 0) * 100}%`,
    top: root ? 0 : `${(props.y || 0) * 100}%`,
    width: root ? `${rootWidth}px` : `${(props.width || 0) * 100}%`,
    height: root ? `${rootHeight}px` : `${(props.height || 0) * 100}%`,
    zIndex: props.zIndex || 0,
    background: token(props.backgroundToken),
  };

  const beginPointerAction = useCallback((event, mode) => {
    if (!editMode || root || event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    onSelect?.(widget.id);
    const start = { clientX: event.clientX, clientY: event.clientY, props: { ...props }, latest: null };
    dragRef.current = start;

    const move = (moveEvent) => {
      const dx = (moveEvent.clientX - start.clientX) / canvasScale;
      const dy = (moveEvent.clientY - start.clientY) / canvasScale;
      const patch = mode === 'resize'
        ? { width: Math.max(0.005, start.props.width + dx / parentWidth), height: Math.max(0.005, start.props.height + dy / parentHeight) }
        : { x: start.props.x + dx / parentWidth, y: start.props.y + dy / parentHeight };
      start.latest = patch;
      onPreview?.(widget.id, patch);
    };
    const end = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
      if (start.latest) onCommit?.(widget.id, start.latest, mode === 'resize' ? `Resize ${props.label || widget.id}` : `Move ${props.label || widget.id}`);
      dragRef.current = null;
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end, { once: true });
  }, [canvasScale, editMode, onCommit, onPreview, onSelect, parentHeight, parentWidth, props, root, widget.id]);

  if (!resolved.visible) return null;

  const frameProps = {
    class: `widget-frame widget-${widget.type.toLowerCase()} ${editMode ? 'is-editable' : ''} ${selectedId === widget.id ? 'is-selected' : ''}`,
    style: frameStyle,
    'data-widget-id': widget.id,
    'data-testid': `widget-${widget.id}`,
    onPointerDown: (event) => beginPointerAction(event, 'drag'),
  };

  let content = null;
  if (widget.type === 'Stack') {
    content = (widget.children || []).map((child) => (
      <WidgetRenderer
        key={child.id}
        widget={child}
        editMode={editMode}
        selectedId={selectedId}
        canvasScale={canvasScale}
        rootWidth={rootWidth}
        rootHeight={rootHeight}
        parentWidth={ownWidth}
        parentHeight={ownHeight}
        activeProfile={activeProfile}
        onSelect={onSelect}
        onPreview={onPreview}
        onCommit={onCommit}
      />
    ));
  } else if (widget.type === 'Text') {
    const textStyle = {
      color: token(props.colorToken, 'var(--color-ink)'),
      fontSize: `${(props.fontSizeRatio || 0.0125) * rootWidth}px`,
      lineHeight: `${(props.lineHeightRatio || props.fontSizeRatio || 0.0125) * rootWidth}px`,
      fontWeight: props.fontWeight || 400,
      fontStyle: props.italic ? 'italic' : 'normal',
      textAlign: props.align || 'left',
      letterSpacing: `${(props.letterSpacingRatio || 0) * rootWidth}px`,
    };
    content = props.href
      ? <a class="widget-text-content" href={props.href} style={textStyle} onClick={(event) => editMode && event.preventDefault()}>{props.text}</a>
      : <div class="widget-text-content" style={textStyle}>{props.text}</div>;
  } else if (widget.type === 'Image') {
    content = <img class="widget-image-content" src={props.src} alt={props.alt || ''} style={{ objectFit: props.objectFit || 'cover' }} />;
  } else if (widget.type === 'Star') {
    content = <div class="star-shape" style={{ background: token(props.colorToken, 'var(--color-ink)') }} aria-hidden="true" />;
  } else if (widget.type === 'Rating') {
    content = (
      <div class="rating-row" aria-label={`${props.count || 0} out of 4`}>
        {Array.from({ length: props.count || 0 }, (_, index) => (
          <span key={index} class="rating-star" style={{ background: token(props.colorToken, 'var(--color-ink)') }} />
        ))}
      </div>
    );
  }

  return (
    <div {...frameProps}>
      {content}
      {editMode && !root && selectedId === widget.id && (
        <button
          type="button"
          class="resize-handle"
          aria-label={`Resize ${props.label || widget.id}`}
          data-testid={`resize-${widget.id}`}
          onPointerDown={(event) => beginPointerAction(event, 'resize')}
        />
      )}
    </div>
  );
}
